/**
 * diagnostics.ts — Step 2: CMY subtractive diagnostics
 *
 * extractAnchors       — find device white/black corners from N-group patches
 * computeDiagnostics   — MeasuredPatch[] → DiagnosticReport
 */

import type {
  DeviceAnchors,
  DiagnosticReport,
  GamutCurve,
  GamutDiagnostic,
  GrayBalanceDiagnostic,
  GrayBalanceStep,
  MeasuredPatch,
  NNSensitivity,
  PatchGroupId,
  Result,
  WorkingDomain,
} from '@/types';

import { ok, err } from '@/types';

// ============================================================================
// Constants
// ============================================================================

const CHROMA_MIN      = 8.0;
const HUE_ANOMALY     = 3.0;   // primary curves: any shift > 3° is anomalous
const HUE_REVERSAL    = 5.0;   // overprint curves: direction reversal > 5° is anomalous
const CHROMA_COLLAPSE = 0.20;

// Signed hue difference (–180 … +180), preserves sweep direction.
function signedHueDiff(hNew: number, hPrev: number): number {
  let d = hNew - hPrev;
  if (d >  180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

// ============================================================================
// Device anchor extraction (Step 2a)
// ============================================================================

export function extractAnchors(patches: MeasuredPatch[]): Result<DeviceAnchors> {
  const nPatches = patches.filter(p => p.group === 'N');
  if (nPatches.length === 0) return err('MISSING_BLACK_POINT');

  const white = nPatches.reduce((a, b) => b.lab[0] > a.lab[0] ? b : a);
  const black = nPatches.reduce((a, b) => b.lab[0] < a.lab[0] ? b : a);

  if (white.lab[0] - black.lab[0] < 50) return err('ANCHOR_VIOLATION');

  return ok({
    white: { L: white.lab[0], a: white.lab[1], b: white.lab[2] },
    black: { L: black.lab[0], a: black.lab[1], b: black.lab[2] },
  });
}

// ============================================================================
// Primary ink level (CMY subtractive model)
// ============================================================================

// Returns the single-channel ink level for each group — always in [0, 1].
// For overprints (RG: C=M, RB: C=Y, GB: M=Y, RGB: C=M=Y) one representative
// channel is returned so all axes share the same [0, 1] scale in RGB cube space.
export function primaryInkLevel(p: MeasuredPatch): number {
  const [C, M, Y] = p.cmy;
  switch (p.group) {
    case 'R':   return C;
    case 'G':   return M;
    case 'B':   return Y;
    case 'RG':  return C;   // C=M by design (R=G)
    case 'RB':  return C;   // C=Y by design (R=B)
    case 'GB':  return M;   // M=Y by design (G=B)
    case 'RGB': return C;   // C=M=Y by design (R=G=B)
    default:    return C;
  }
}

// ============================================================================
// Gamut curve builder
// ============================================================================

export function buildGamutCurve(
  patches: MeasuredPatch[],
  group:   PatchGroupId,
  label:   'C' | 'M' | 'Y' | 'CM' | 'CY' | 'MY' | 'CMY',
): GamutCurve {
  const pts = patches
    .filter(p => p.group === group)
    .sort((a, b) => primaryInkLevel(a) - primaryInkLevel(b));

  // Overprint curves sweep through wide hue arcs as ink increases — that's normal.
  // Only flag DIRECTION REVERSALS (sign flip of the signed hue delta).
  // Primary curves (C, M, Y) should keep a constant hue — any shift > 3° is anomalous.
  const isOverprint = label === 'CM' || label === 'CY' || label === 'MY' || label === 'CMY';

  let prevHue      = NaN;
  let prevChroma   = 0;
  let prevSignedDH = NaN;

  const points = pts.map(p => {
    const [L, a, b] = p.lab;
    const hue    = ((Math.atan2(b, a) * 180 / Math.PI) + 360) % 360;
    const chroma = Math.sqrt(a * a + b * b);
    const ink    = primaryInkLevel(p);

    const signedDH = !isNaN(prevHue) ? signedHueDiff(hue, prevHue) : 0;
    const dHue     = Math.abs(signedDH);
    const dChroma  = prevChroma > 0 ? chroma - prevChroma : 0;

    let hueAnomaly: boolean;
    if (isOverprint) {
      // Direction reversal: sweep changes sign AND magnitude exceeds threshold
      hueAnomaly = !isNaN(prevSignedDH) && prevSignedDH !== 0
        && signedDH * prevSignedDH < 0
        && dHue > HUE_REVERSAL
        && chroma > CHROMA_MIN && prevChroma > CHROMA_MIN;
    } else {
      hueAnomaly = dHue > HUE_ANOMALY && chroma > CHROMA_MIN && prevChroma > CHROMA_MIN;
    }
    const chromaAnomaly = prevChroma > CHROMA_MIN && dChroma < -CHROMA_COLLAPSE * prevChroma;

    if (hueAnomaly)    p.flags.hueAnomaly    = true;
    if (chromaAnomaly) p.flags.chromaAnomaly = true;

    prevHue      = hue;
    prevChroma   = chroma;
    prevSignedDH = signedDH;

    return {
      patchId: p.id,
      ink, a, b, L,
      hueAngle: hue, chroma, dHue, dChroma,
      flags: { hueAnomaly, chromaAnomaly },
    };
  });

  const maxChroma = Math.max(...points.map(p => p.chroma), 0);
  const last      = points.at(-1);

  return {
    label,
    points,
    maxChroma,
    maxInk:     last?.ink     ?? 0,
    gamutPoint: { a: last?.a ?? 0, b: last?.b ?? 0, L: last?.L ?? 0 },
  };
}

// ============================================================================
// NN Sensitivity Jacobian
// ============================================================================

function twoSided(
  vPlus:   number | undefined,
  vMinus:  number | undefined,
  vCenter: number,
  delta:   number,
): number {
  if (vPlus !== undefined && vMinus !== undefined) return (vPlus - vMinus) / (2 * delta);
  if (vPlus !== undefined)  return (vPlus  - vCenter) / delta;
  if (vMinus !== undefined) return (vCenter - vMinus) / delta;
  return 0;
}

export function computeNNSensitivity(
  patches: MeasuredPatch[],
  level:   number,
): NNSensitivity | null {
  const DEL = 8;

  const center = patches.find(
    p => p.rgb[0] === level && p.rgb[1] === level && p.rgb[2] === level,
  );
  if (!center) return null;

  const pRp = patches.find(p => p.rgb[0]===level+DEL && p.rgb[1]===level   && p.rgb[2]===level);
  const pRm = patches.find(p => p.rgb[0]===level-DEL && p.rgb[1]===level   && p.rgb[2]===level);
  const pBp = patches.find(p => p.rgb[0]===level     && p.rgb[1]===level   && p.rgb[2]===level+DEL);
  const pBm = patches.find(p => p.rgb[0]===level     && p.rgb[1]===level   && p.rgb[2]===level-DEL);

  if (!pRp && !pRm) return null;
  if (!pBp && !pBm) return null;

  const [, ca, cb] = center.lab;

  const dAdR = twoSided(pRp?.lab[1], pRm?.lab[1], ca, DEL);
  const dBdR = twoSided(pRp?.lab[2], pRm?.lab[2], cb, DEL);
  const dAdB = twoSided(pBp?.lab[1], pBm?.lab[1], ca, DEL);
  const dBdB = twoSided(pBp?.lab[2], pBm?.lab[2], cb, DEL);
  const det  = dAdR * dBdB - dAdB * dBdR;

  return {
    level,
    center: { L: center.lab[0], a: ca, b: cb },
    dAdR, dAdB, dBdR, dBdB, det,
  };
}

function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

function interpolateNN(list: NNSensitivity[], gRgb: number): NNSensitivity | null {
  if (!list.length) return null;
  const sorted = [...list].sort((a, b) => a.level - b.level);
  const above  = sorted.find(s => s.level >= gRgb);
  const below  = [...sorted].reverse().find(s => s.level <= gRgb);
  if (!above) return below ?? null;
  if (!below) return above;
  if (above === below) return above;
  const t = (gRgb - below.level) / (above.level - below.level);
  return {
    level: gRgb,
    center: {
      L: lerp(below.center.L, above.center.L, t),
      a: lerp(below.center.a, above.center.a, t),
      b: lerp(below.center.b, above.center.b, t),
    },
    dAdR: lerp(below.dAdR, above.dAdR, t),
    dAdB: lerp(below.dAdB, above.dAdB, t),
    dBdR: lerp(below.dBdR, above.dBdR, t),
    dBdB: lerp(below.dBdB, above.dBdB, t),
    det:  lerp(below.det,  above.det,  t),
  };
}

// ============================================================================
// Ramp-based Jacobian fallback
// ============================================================================

function estimateFromRamps(patches: MeasuredPatch[], level: number): NNSensitivity | null {
  const center = patches.find(
    p => p.rgb[0] === level && p.rgb[1] === level && p.rgb[2] === level,
  );
  if (!center) return null;

  const rGroup = patches.filter(p => p.group === 'R').sort((a, b) => a.rgb[0] - b.rgb[0]);
  const bGroup = patches.filter(p => p.group === 'B').sort((a, b) => a.rgb[2] - b.rgb[2]);

  const rBelow = [...rGroup].reverse().find(p => p.rgb[0] < level);
  const rAbove = rGroup.find(p => p.rgb[0] > level);
  const bBelow = [...bGroup].reverse().find(p => p.rgb[2] < level);
  const bAbove = bGroup.find(p => p.rgb[2] > level);

  if (!rBelow || !rAbove || !bBelow || !bAbove) return null;

  const dR = rAbove.rgb[0] - rBelow.rgb[0];
  const dB = bAbove.rgb[2] - bBelow.rgb[2];
  if (dR === 0 || dB === 0) return null;

  const dAdR = (rAbove.lab[1] - rBelow.lab[1]) / dR;
  const dBdR = (rAbove.lab[2] - rBelow.lab[2]) / dR;
  const dAdB = (bAbove.lab[1] - bBelow.lab[1]) / dB;
  const dBdB = (bAbove.lab[2] - bBelow.lab[2]) / dB;
  const det  = dAdR * dBdB - dAdB * dBdR;

  return {
    level,
    center: { L: center.lab[0], a: center.lab[1], b: center.lab[2] },
    dAdR, dBdR, dAdB, dBdB, det,
  };
}

// ============================================================================
// Gray balance
// ============================================================================

function buildGrayBalance(
  patches:    MeasuredPatch[],
  nnSens:     NNSensitivity[],
  paperWhite: { L: number; a: number; b: number },
): GrayBalanceDiagnostic {
  const { a: a0, b: b0 } = paperWhite;

  const nPatches = patches
    .filter(p => p.group === 'N' && p.rgb[0] === p.rgb[1] && p.rgb[1] === p.rgb[2])
    .sort((a, b) => b.lab[0] - a.lab[0]);

  const steps: GrayBalanceStep[] = nPatches.map(p => {
    const gRgb         = p.rgb[1];
    const [L, aMeas, bMeas] = p.lab;
    const aCast  = aMeas - a0;
    const bCast  = bMeas - b0;
    const deltaE = Math.sqrt(aCast * aCast + bCast * bCast);

    const nn = interpolateNN(nnSens, gRgb) ?? estimateFromRamps(patches, gRgb);
    let dRFromG: number | null = null;
    let dBFromG: number | null = null;

    if (nn && Math.abs(nn.det) > 1e-4) {
      dRFromG = (-nn.dBdB * aCast + nn.dAdB * bCast) / nn.det;
      dBFromG = ( nn.dBdR * aCast - nn.dAdR * bCast) / nn.det;
    }

    return {
      patchId: p.id,
      gRgb,
      mInk:   1 - gRgb / 255,
      L,
      aCast, bCast, deltaE,
      dRFromG, dBFromG,
      dCInk: dRFromG !== null ? -dRFromG / 255 : null,
      dYInk: dBFromG !== null ? -dBFromG / 255 : null,
    };
  });

  const maxCast   = steps.reduce((m, s) => Math.max(m, s.deltaE), 0);
  const meanBCast = steps.length > 0
    ? steps.reduce((s, x) => s + x.bCast, 0) / steps.length
    : 0;

  return {
    paperWhite,
    steps,
    maxCast,
    dominantCast: meanBCast > 0.5 ? 'warm' : meanBCast < -0.5 ? 'cool' : 'neutral',
  };
}

// ============================================================================
// Auto-suggest domain
// ============================================================================

function inkLimit(curve: GamutCurve): number {
  const lastGood = [...curve.points]
    .reverse()
    .find(p => !p.flags.hueAnomaly && !p.flags.chromaAnomaly);
  return lastGood?.ink ?? curve.maxInk;
}

// inkLimit() returns per-channel axis parameter ∈ [0,1]. WorkingDomain stores
// the same axis-parameter values directly — no multiplier needed for overprints.
export function suggestDomain(gamut: GamutDiagnostic): WorkingDomain {
  const cMax  = Math.min(inkLimit(gamut.curves.C),  1.0);
  const mMax  = Math.min(inkLimit(gamut.curves.M),  1.0);
  const yMax  = Math.min(inkLimit(gamut.curves.Y),  1.0);
  const cmMax = Math.min(inkLimit(gamut.curves.CM), 1.0);
  const cyMax = Math.min(inkLimit(gamut.curves.CY), 1.0);
  const myMax = Math.min(inkLimit(gamut.curves.MY), 1.0);
  const cmyMax = gamut.curves.CMY
    ? Math.min(inkLimit(gamut.curves.CMY), 1.0)
    : Math.min((cMax + mMax + yMax) / 3, 1.0);
  return { cMax, mMax, yMax, cmMax, cyMax, myMax, cmyMax, userOverride: false };
}

// ============================================================================
// Main entry point
// ============================================================================

const NN_LEVELS = [32, 64, 96, 128, 160, 192, 224] as const;

export function computeDiagnostics(patches: MeasuredPatch[]): DiagnosticReport {
  // Step 2a: device anchors
  const anchorsResult = extractAnchors(patches);
  if (!anchorsResult.ok) throw new Error(anchorsResult.error);
  const anchors = anchorsResult.value;

  // Paper white: prefer the patch at rgb=255,255,255 so the gamut chart origin
  // aligns exactly with where all ramp curves start; fall back to brightest N patch.
  const paperPatch =
    patches.find(p => p.rgb[0] === 255 && p.rgb[1] === 255 && p.rgb[2] === 255)
    ?? patches
        .filter(p => p.group === 'N')
        .reduce<MeasuredPatch | null>(
          (best, p) => (!best || p.lab[0] > best.lab[0]) ? p : best,
          null,
        );

  const paperWhite = {
    L: paperPatch?.lab[0] ?? anchors.white.L,
    a: paperPatch?.lab[1] ?? anchors.white.a,
    b: paperPatch?.lab[2] ?? anchors.white.b,
  };

  // Gamut curves
  const gamutCurves: GamutDiagnostic['curves'] = {
    C:  buildGamutCurve(patches, 'R',  'C'),
    M:  buildGamutCurve(patches, 'G',  'M'),
    Y:  buildGamutCurve(patches, 'B',  'Y'),
    CM: buildGamutCurve(patches, 'RG', 'CM'),
    CY: buildGamutCurve(patches, 'RB', 'CY'),
    MY: buildGamutCurve(patches, 'GB', 'MY'),
    ...(patches.some(p => p.group === 'RGB')
      ? { CMY: buildGamutCurve(patches, 'RGB', 'CMY') }
      : {}),
  };
  const gamut: GamutDiagnostic = { curves: gamutCurves };

  // NN Jacobians
  const nnSens: NNSensitivity[] = [];
  for (const level of NN_LEVELS) {
    const s = computeNNSensitivity(patches, level);
    if (s) nnSens.push(s);
  }

  // Gray balance
  const grayBalance = buildGrayBalance(patches, nnSens, paperWhite);

  // Flagged patch IDs
  const flaggedPatchIds = patches
    .filter(p => p.flags.hueAnomaly || p.flags.chromaAnomaly)
    .map(p => p.id);

  // Auto-suggest domain
  const recommendedDomain = suggestDomain(gamut);

  return {
    gamut,
    grayBalance,
    paperWhite,
    paperBlack: anchors.black,
    anchors,
    flaggedPatchIds,
    recommendedDomain,
  };
}
